import { DEFAULT_BASE_TOKEN, resolveBaseToken } from '../confs/app';
import { APPBaseTokenConfig } from '../types/app';
import { mergeAppSetting, partializeAppSetting } from './app-setting-persist';
import { combine, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

export const saveKey = 'app-setting';

// store
export const useAppSettingStore = createWithEqualityFn(
  persist(
    combine(
      {
        onboardOpen: true,
        // Global currency lens: the base token the whole app operates in
        // (feed, ticker, balances). Per-market pages still use the market's
        // own collateral.
        baseToken: DEFAULT_BASE_TOKEN,
        // Session-only counter (never persisted): >0 means a detail page pins
        // the lens to its market's collateral, so user-facing switchers are
        // inert. A counter — not a boolean — because detail mounts overlap
        // (page-to-page navigation, StrictMode double-mount) and the first
        // unmount must not unlock the page still being viewed.
        lensLockCount: 0,
      },
      (set, get) => ({
        setOnboardOpen: (onboardOpen: boolean) => set({ onboardOpen }),
        setBaseToken: (baseToken: APPBaseTokenConfig) => {
          if (get().lensLockCount > 0) return;
          set({ baseToken });
        },
        setBaseTokenAddress: (address: string) => {
          if (get().lensLockCount > 0) return;
          set({ baseToken: resolveBaseToken(address) || DEFAULT_BASE_TOKEN });
        },
        // Lock-bypassing write, reserved for the lens-sync hook (pin on enter,
        // restore on leave). UI switchers must go through setBaseToken*.
        forceBaseTokenAddress: (address: string) => set({ baseToken: resolveBaseToken(address) || DEFAULT_BASE_TOKEN }),
        acquireLensLock: () => set({ lensLockCount: get().lensLockCount + 1 }),
        releaseLensLock: () => set({ lensLockCount: Math.max(0, get().lensLockCount - 1) }),
      })
    ),
    {
      name: saveKey,
      // v3 reset persisted baseToken (stale testnet TUSDC address); v4 drops the
      // short-lived feedTokenFilter key and switches to address-only persistence.
      version: 4,
      migrate: () => ({ onboardOpen: true, baseTokenAddress: DEFAULT_BASE_TOKEN.address }),
      // why address-only persistence + legacy-shape recovery: see app-setting-persist.ts
      partialize: partializeAppSetting,
      merge: mergeAppSetting(resolveBaseToken, DEFAULT_BASE_TOKEN),
    }
  ),
  shallow
);

export const useAppStore = createWithEqualityFn(
  combine(
    {
      currentTag: 'trending',
      currentTags: [] as string[],
      scrollTo: undefined as string | undefined,
      lastId: undefined as number | undefined,
    },
    set => ({
      setCurrentTag: (tag: string) => set({ currentTag: tag }),
      setCurrentTags: (tags: string[]) => set({ currentTags: tags }),
      setLastId: (lastId?: number) => set({ lastId }),
      setScrollTo: (scrollTo?: string) => set({ scrollTo }),
    })
  ),
  shallow
);
